import { useState } from "react";
import { toast } from "sonner";
import { inputCls, Field, CheckGroup, GoldButton, meetsMinimums, Honeypot, PrivacyNote } from "./fields";
import { useDraft } from "./useMember";
import { useSubmitApplication } from "./useSubmitApplication";

const EMPTY = {
  name: "", hometown: "", license_level: "", license_number: "", experience: "",
  interests: [], bio: "", video_url: "",
};

export const CaptainForm = ({ options, headers, onCreated }) => {
  const [form, setForm, clear] = useDraft("cc_draft_captain", EMPTY);
  const { busy, submit } = useSubmitApplication({ path: "captain", headers, onCreated, clearDraft: clear });
  const [showErrors, setShowErrors] = useState(false);
  const [website, setWebsite] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const MINS = [[form.name, 2], [form.license_number, 3], [form.experience, 10]];

  const onSubmit = (e) => {
    e.preventDefault();
    if (!meetsMinimums(MINS) || !form.license_level) { setShowErrors(true); toast.error("Please complete the highlighted fields."); return; }
    submit({ ...form, website }, form.name);
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="captain-form">
      <p className="text-xs text-[#94A3B8] border border-[#D4AF37]/25 bg-[#070A0F] p-3 leading-relaxed" data-testid="captain-license-note">
        Captains must hold a current USCG license. Entering a license number records what you hold — it is
        not independently verified at this stage.
      </p>
      <Field label="Full name" value={form.name} minLength={2} showErrors={showErrors} testId="captain-name">
        <input required value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} data-testid="captain-name" />
      </Field>
      <Field label="Hometown" hint="optional">
        <input value={form.hometown} onChange={set("hometown")} placeholder="e.g. San Diego, CA" className={inputCls} data-testid="captain-hometown" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="USCG license level" value={form.license_level} showErrors={showErrors} testId="captain-license-level" error="Please select your USCG license level">
          <select required value={form.license_level} onChange={set("license_level")} className={inputCls} data-testid="captain-license-level">
            <option value="" disabled>Select license level</option>
            {options.license_levels.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
        <Field label="USCG license / documentation number" value={form.license_number} minLength={3} showErrors={showErrors} testId="captain-license-number">
          <input required value={form.license_number} onChange={set("license_number")} placeholder="Merchant Mariner Credential number"
            className={inputCls} data-testid="captain-license-number" />
        </Field>
      </div>
      <Field label="Maritime experience" value={form.experience} minLength={10} showErrors={showErrors} testId="captain-experience">
        <textarea required rows={4} value={form.experience} onChange={set("experience")}
          placeholder="Vessels, waters, years at the helm, deliveries, charters…" className={inputCls} data-testid="captain-experience" />
      </Field>
      <CheckGroup label="Experience & interests" hint="check all that apply" options={options.captain_interests}
        value={form.interests} onChange={(v) => setForm((f) => ({ ...f, interests: v }))} testPrefix="captain-interest" />
      <Field label="Your sea story" hint="optional — shown on your public card">
        <textarea rows={3} value={form.bio} onChange={set("bio")} placeholder="Why do you deserve a berth?" className={inputCls} data-testid="captain-bio" />
      </Field>
      <Field label="Audition video URL" hint="YouTube or Vimeo — optional">
        <input value={form.video_url} onChange={set("video_url")} placeholder="https://youtube.com/…" className={inputCls} data-testid="captain-video" />
      </Field>
      <Honeypot value={website} onChange={(e) => setWebsite(e.target.value)} />
      <PrivacyNote>Your name, hometown, sea story, interests and license level appear on your public voting card; your license number and email stay private and are kept only for casting.</PrivacyNote>
      <GoldButton type="submit" disabled={busy} data-testid="captain-submit">{busy ? "SENDING…" : "SUBMIT CAPTAIN APPLICATION"}</GoldButton>
    </form>
  );
};
